import { Shield } from 'lucide-react';
import { useMemo, useState } from 'react';
import { api } from '../api';
import { ChipSelect, Field, Loading } from '../components';
import { defaultStartValue } from '../format';
import { useRouter } from '../router';
import { useApp } from '../store';
import { PLAY_STYLE_LABELS, VOICE_LABELS, type PlayStyle, type VoicePreference } from '../types';

export function NewSession() {
  const { games, profile, toast } = useApp();
  const { navigate } = useRouter();

  const [gameSlug, setGameSlug] = useState('');
  const [activityId, setActivityId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startValue, setStartValue] = useState(defaultStartValue());
  const [duration, setDuration] = useState(60);
  const [partySize, setPartySize] = useState(3);
  const [roles, setRoles] = useState<string[]>([]);
  const [platform, setPlatform] = useState('Crossplay');
  const [voice, setVoice] = useState<VoicePreference>('optional');
  const [style, setStyle] = useState<PlayStyle>(profile?.playStyle ?? 'balanced');
  const [repeatWeekly, setRepeatWeekly] = useState(false);
  const [membersOnly, setMembersOnly] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const guildSlug = useMemo(() => new URLSearchParams(window.location.search).get('guild') ?? '', []);
  const game = useMemo(() => games.find((item) => item.slug === gameSlug) ?? null, [games, gameSlug]);
  const activity = game?.activities.find((item) => item.id === activityId) ?? null;

  if (games.length === 0) return <Loading label="Loading game catalog…" />;

  function pickGame(slug: string) {
    setGameSlug(slug);
    setActivityId('');
    setRoles([]);
    const next = games.find((item) => item.slug === slug);
    if (next) {
      setPartySize(Math.min(3, next.maxParty));
      setPlatform(next.platforms[0] ?? 'Crossplay');
    }
  }

  function pickActivity(id: string) {
    setActivityId(id);
    const picked = game?.activities.find((item) => item.id === id);
    if (picked) {
      setDuration(picked.defaultDurationMinutes);
      if (!title) setTitle(picked.name);
    }
  }

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!game) { setErrorMessage('Pick a game first.'); return; }
    setBusy(true);
    setErrorMessage('');
    try {
      const startTime = new Date(startValue).toISOString();
      const created = await api.createSession({
        game: game.slug,
        activityId: activityId || undefined,
        title,
        description,
        startTime,
        durationMinutes: duration,
        partySize,
        requiredRoles: roles,
        platform,
        voice,
        style,
        repeatWeekly,
        ...(guildSlug ? { guildSlug, membersOnly } : {})
      });
      toast('Session created. Share it so your squad can join.', 'success');
      navigate(`/sessions/${created.session.id}?created=1`);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Could not create the session.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page narrow">
      <div className="section-heading">
        <div>
          <span className="eyebrow">{guildSlug ? 'Guild session' : 'Host'}</span>
          <h1>Create a session</h1>
        </div>
      </div>
      {guildSlug && <div className="notice"><Shield size={15} /> Hosting for guild <strong>{guildSlug}</strong> — it appears on the guild calendar.</div>}
      <form className="stack-form" onSubmit={submit}>
        <Field label="Game">
          <ChipSelect
            options={games.map((item) => ({ value: item.slug, label: item.name }))}
            selected={gameSlug ? [gameSlug] : []}
            onToggle={pickGame}
            single
          />
        </Field>

        {game && (
          <>
            <Field label="Activity">
              <ChipSelect
                options={game.activities.map((item) => ({ value: item.id, label: item.name }))}
                selected={activityId ? [activityId] : []}
                onToggle={pickActivity}
                single
              />
            </Field>

            <Field label="Title">
              <input value={title} onChange={(event) => setTitle(event.target.value)} minLength={4} maxLength={80} placeholder={activity ? `${activity.name} — chill run` : 'What are you running?'} required />
            </Field>

            <Field label="Description" hint="Expectations, experience level, loadouts — whatever helps the right people join.">
              <textarea value={description} onChange={(event) => setDescription(event.target.value)} maxLength={500} rows={3} />
            </Field>

            <div className="field-row">
              <Field label="Start time">
                <input type="datetime-local" value={startValue} onChange={(event) => setStartValue(event.target.value)} required />
              </Field>
              <Field label="Duration (minutes)">
                <input type="number" min={15} max={360} step={15} value={duration} onChange={(event) => setDuration(Number(event.target.value))} />
              </Field>
              <Field label={`Party size (max ${game.maxParty})`}>
                <input type="number" min={2} max={game.maxParty} value={partySize} onChange={(event) => setPartySize(Number(event.target.value))} />
              </Field>
            </div>

            <Field label="Looking for" hint="Optional roles you want filled.">
              <ChipSelect
                options={game.roles.map((role) => ({ value: role, label: role }))}
                selected={roles}
                onToggle={(role) => setRoles((current) => current.includes(role) ? current.filter((item) => item !== role) : [...current, role])}
              />
            </Field>

            <div className="field-row">
              <Field label="Platform">
                <ChipSelect
                  options={game.platforms.map((item) => ({ value: item, label: item }))}
                  selected={[platform]}
                  onToggle={setPlatform}
                  single
                />
              </Field>
            </div>
            <div className="field-row">
              <Field label="Voice chat">
                <ChipSelect
                  options={(Object.keys(VOICE_LABELS) as VoicePreference[]).map((value) => ({ value, label: VOICE_LABELS[value] }))}
                  selected={[voice]}
                  onToggle={(value) => setVoice(value as VoicePreference)}
                  single
                />
              </Field>
              <Field label="Play style">
                <ChipSelect
                  options={(Object.keys(PLAY_STYLE_LABELS) as PlayStyle[]).map((value) => ({ value, label: PLAY_STYLE_LABELS[value] }))}
                  selected={[style]}
                  onToggle={(value) => setStyle(value as PlayStyle)}
                  single
                />
              </Field>
            </div>

            <Field label="Options">
              <div className="toggle-row">
                <button type="button" role="switch" aria-checked={repeatWeekly} className={`chip ${repeatWeekly ? 'active' : ''}`} onClick={() => setRepeatWeekly((current) => !current)}>
                  {repeatWeekly ? 'Repeats weekly' : 'One time'}
                </button>
                {guildSlug && (
                  <button type="button" role="switch" aria-checked={membersOnly} className={`chip ${membersOnly ? 'active' : ''}`} onClick={() => setMembersOnly((current) => !current)}>
                    {membersOnly ? 'Guild members only' : 'Open to everyone'}
                  </button>
                )}
              </div>
            </Field>

            {errorMessage && <div className="form-error" role="alert">{errorMessage}</div>}
            <button className="primary" type="submit" disabled={busy}>{busy ? 'Creating…' : 'Create session'}</button>
          </>
        )}
      </form>
    </div>
  );
}
